import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import { useDebouncedCallback } from "use-debounce";

import { MapContext } from "fond/map/MapProvider";
import { BBox } from "fond/types/geometry";

interface UseMapBoundsProps {
  skip?: boolean;
}

interface UseMapBoundsReturns {
  bounds: BBox | null;
  zoom: number | null;
}

/**
 * Hook that tracks the current visible bounds of a Mapbox map instance.
 *
 * Provides updates of the map's viewport boundaries as the user pans/zooms.
 * Useful for loading data dynamically based on visible area.
 *
 * @param skip - When true, disables bound tracking to prevent unnecessary updates
 * @returns Current map bounds as a BBox (southwest and northeast coordinates) or null if map not ready
 */
export const useMapBounds = ({ skip }: UseMapBoundsProps): UseMapBoundsReturns => {
  const map = useContext(MapContext)?.map;
  const [mapBounds, setMapBounds] = useState<BBox | null>(null);
  const [mapZoom, setMapZoom] = useState<number | null>(null);

  const updateBoundsFromMap = useCallback(() => {
    if (!map) return;
    const bounds = map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const bbox: BBox = [
      [sw.lng, sw.lat],
      [ne.lng, ne.lat],
    ];
    if (!mapBounds || !isEqual(mapBounds, bbox)) {
      setMapBounds(bbox);
    }
    setMapZoom(Math.floor(map.getZoom()));
  }, [map, mapBounds]);

  // Debouncing prevents excessive API calls or expensive computations that depend on bounds
  // during continuous panning/zooming interactions
  const onMoveMap = useDebouncedCallback(updateBoundsFromMap, 500);

  // When the map stops, flush any pending updates immediately.
  const onMapIdle = useCallback(() => {
    if (onMoveMap.isPending()) {
      onMoveMap.flush();
    }
  }, [onMoveMap]);

  // Update bounds whenever the map moves
  useEffect(() => {
    if (skip || !map) return;

    // Trigger the initial bounds update when the map first loads.
    onMoveMap();

    map.on("move", onMoveMap);
    map.on("idle", onMapIdle);
    return () => {
      map.off("move", onMoveMap);
      map.off("idle", onMapIdle);
    };
  }, [map, skip, onMoveMap, onMapIdle]);

  return useMemo(() => ({ bounds: mapBounds, zoom: mapZoom }), [mapBounds, mapZoom]);
};
